import React, { useState, useEffect } from 'react';
import { LockerFloor, LockerFloorFormData } from '../types/locker';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface LockerFloorFormSliderProps {
  workspaceId: string;
  floorId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

const LockerFloorFormSlider: React.FC<LockerFloorFormSliderProps> = ({
  workspaceId,
  floorId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [initialProjectId, setInitialProjectId] = useState('');

  const initialFormData: LockerFloorFormData = {
    projectId: initialProjectId,
    name: '',
    total: '0',
    available: '0',
    marketingHold: '0',
    salesHold: '0',
    sold: '0',
    customerSelection: '0',
    customerSubmitted: '0'
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project if creating new floor
      if (!floorId && projectsList.length > 0) {
        setInitialProjectId(projectsList[0]._id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  const validateForm = (data: LockerFloorFormData): string | null => {
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.name.trim()) {
      return 'Locker floor name is required';
    }
    return null;
  };

  const transformFetchData = (response: any): LockerFloorFormData => {
    const floor: LockerFloor = response.lockerFloor;
    return {
      projectId: floor.projectId || '',
      name: floor.name || '',
      total: floor.total?.toString() || '0',
      available: floor.available?.toString() || '0',
      marketingHold: floor.marketingHold?.toString() || '0',
      salesHold: floor.salesHold?.toString() || '0',
      sold: floor.sold?.toString() || '0',
      customerSelection: floor.customerSelection?.toString() || '0',
      customerSubmitted: floor.customerSubmitted?.toString() || '0'
    };
  };

  const transformSubmitData = (data: LockerFloorFormData) => {
    return {
      projectId: data.projectId,
      name: data.name.trim(),
      total: data.total ? parseInt(data.total) : 0,
      available: data.available ? parseInt(data.available) : 0,
      marketingHold: data.marketingHold ? parseInt(data.marketingHold) : 0,
      salesHold: data.salesHold ? parseInt(data.salesHold) : 0,
      sold: data.sold ? parseInt(data.sold) : 0,
      customerSelection: data.customerSelection ? parseInt(data.customerSelection) : 0,
      customerSubmitted: data.customerSubmitted ? parseInt(data.customerSubmitted) : 0
    };
  };

  if (!floorId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={initialProjectId}
      workspaceId={workspaceId}
      itemId={floorId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/locker-floors`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="locker-floor-form"
    >
      {({ formData, handleInputChange, loading }) => (
        <>
          {/* Project */}
          <div className="form-group">
            <label htmlFor="projectId" className="form-label">
              Project
            </label>
            {loadingProjects ? (
              <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                <div className="builders-loading-spinner" />
              </div>
            ) : (
              <select
                id="projectId"
                name="projectId"
                className="form-input"
                value={formData.projectId}
                onChange={handleInputChange}
                required
                disabled={loading}
              >
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Name */}
          <div className="form-group">
            <label htmlFor="name" className="form-label">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
            />
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default LockerFloorFormSlider;
